import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(m.matches);
    on();
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

// Smooth count-up for hero/KPI numbers (respects reduced motion)
export function useCountUp(target, dur = 800) {
  const reduced = usePrefersReducedMotion();
  const [v, setV] = useState(typeof target === "number" ? 0 : target);
  const prev = useRef(0);
  useEffect(() => {
    if (typeof target !== "number") { setV(target); return; }
    if (reduced) { setV(target); prev.current = target; return; }
    const from = prev.current || 0;
    const t0 = performance.now();
    let raf;
    const loop = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(loop);
      else prev.current = target;
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [target, reduced, dur]);
  return v;
}

export function useLastVisit(tenantId) {
  const key = `pawkt_lastvisit_${tenantId || "x"}`;
  const [prev] = useState(() => {
    try { return localStorage.getItem(key); } catch { return null; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, new Date().toISOString()); } catch {}
  }, [key]);
  return prev; // ISO string of the PREVIOUS visit, or null on first visit
}

export function Panel({ className, children, ...p }) {
  return (
    <div className={cn("rounded-xl border border-pawkt-border bg-white shadow-card", className)} {...p}>
      {children}
    </div>
  );
}

export function Eyebrow({ children, className }) {
  return <p className={cn("text-[11px] font-bold uppercase tracking-[0.08em] text-pawkt-text-secondary", className)}>{children}</p>;
}

export function SectionHead({ eyebrow, title, hint, action }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {title && <h3 className="font-display text-lg font-extrabold text-pawkt-text leading-tight mt-0.5">{title}</h3>}
        {hint && <p className="text-xs text-pawkt-text-secondary mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

export function SignalSkeleton({ lines = 3, className }) {
  return (
    <Panel className={cn("p-5", className)} data-testid="signal-skeleton">
      <div className="h-3 w-24 rounded-full bg-pawkt-elevated animate-pulse" />
      <div className="h-7 w-40 rounded-lg bg-pawkt-elevated animate-pulse mt-3" />
      <div className="mt-4 space-y-2.5">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="h-10 rounded-xl bg-pawkt-soft border border-pawkt-border-soft animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
    </Panel>
  );
}

export function SignalError({ onRetry, label = "Couldn't load this signal right now." }) {
  return (
    <Panel className="p-5" data-testid="signal-error">
      <p className="text-sm text-pawkt-text-secondary">{label}</p>
      <button onClick={onRetry} data-testid="signal-retry"
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-pawkt-border bg-white px-3.5 py-1.5 text-xs font-bold text-pawkt-text hover:bg-pawkt-elevated transition-colors">
        <RefreshCw className="h-3.5 w-3.5" /> Retry
      </button>
    </Panel>
  );
}

// Render-prop shell: isolates each section so one failure never crashes the page
export function SectionShell({ query, skeleton, children }) {
  if (query.isLoading) return skeleton || <SignalSkeleton />;
  if (query.isError) return <SignalError onRetry={() => query.refetch()} />;
  return children(query.data);
}

// Descriptive state helpers (never a fabricated score)
export const STATE_TONE = {
  Healthy: "bg-pawkt-mint text-pawkt-success-deep",
  Growing: "bg-pawkt-mint text-pawkt-success-deep",
  Active: "bg-pawkt-primary-soft text-pawkt-primary-deep",
  Busy: "bg-pawkt-primary-soft text-pawkt-primary-deep",
  "Needs attention": "bg-pawkt-coral text-pawkt-emergency",
  Quiet: "bg-pawkt-elevated text-pawkt-text-secondary",
  "Limited data": "bg-pawkt-elevated text-pawkt-text-secondary",
};
export const STATE_RADIUS = {
  Healthy: 1, Growing: 0.86, Busy: 0.82, Active: 0.8,
  "Needs attention": 0.46, Quiet: 0.5, "Limited data": 0.32,
};

export function StateChip({ state }) {
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold", STATE_TONE[state] || STATE_TONE["Limited data"])}>
      {state}
    </span>
  );
}
